import { auth } from '../../config/firebase';

const API_BASE_URL = 'https://www.mentorist.app/api';

export interface DashboardResponse {
  articles: ArticlesResponse;
  dailyPicks: DailyPicksResponse;
  pickedForYou: PickedForYouResponse;
}

export interface ArticlesResponse {
  params: {
    algorithm: string;
  };
  sections: Array<{
    title: string;
    articles: string[]; // Array of article IDs
  }>;
}

export interface DailyPicksResponse {
  books: string[]; // Array of book IDs
}

export interface PickedForYouResponse {
  books: Array<{
    id: string;
    reason?: string;
  }>;
}

export interface DashboardQuery {
  id: {
    deviceId?: string;
    userId?: string;
  };
  user?: {
    email?: string;
    name?: string;
  };
  articles?: {
    type: 'regular' | 'forNewUser';
    lastFetched?: {
      timestamp: number;
      items: string[];
    };
  };
}

class DashboardAPI {
  private static instance: DashboardAPI;
  private cachedResponse: DashboardResponse | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  private constructor() {}

  static getInstance(): DashboardAPI {
    if (!DashboardAPI.instance) {
      DashboardAPI.instance = new DashboardAPI();
    }
    return DashboardAPI.instance;
  }

  async getDashboardData(forceRefresh = false): Promise<DashboardResponse> {
    const now = Date.now();
    
    // Return cached data if available and not expired
    if (!forceRefresh && this.cachedResponse && (now - this.cacheTimestamp) < this.CACHE_DURATION) {
      return this.cachedResponse;
    }

    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const query: DashboardQuery = {
        id: {
          userId: user.uid,
          deviceId: 'web-' + user.uid // Simple device ID for web
        },
        user: {
          email: user.email || undefined,
          name: user.displayName || undefined
        },
        articles: {
          type: 'regular', // TODO: Check if user is new
          lastFetched: undefined // TODO: Track last fetched articles
        }
      };

      const idToken = await user.getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/dashboard/v1/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(query)
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      // Cache the response
      this.cachedResponse = data;
      this.cacheTimestamp = now;
      
      return data;
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      
      // Return mock data as fallback
      return {
        articles: {
          params: { algorithm: 'fallback' },
          sections: [{
            title: 'Featured Articles',
            articles: ['1', '10', '100'] // IDs from our Firebase data
          }]
        },
        dailyPicks: {
          books: ['10', '100', '1000'] // IDs from our Firebase data
        },
        pickedForYou: {
          books: [
            { id: '10', reason: 'Based on your interests' },
            { id: '100', reason: 'Popular this week' }
          ]
        }
      };
    }
  }

  clearCache(): void {
    this.cachedResponse = null;
    this.cacheTimestamp = 0;
  }
}

export const dashboardAPI = DashboardAPI.getInstance(); 